import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article (Metrics, mapped to the "ROI" goal token).
 * What to measure in a foundry: the metric definitions, a metric row of the
 * headline KPIs, and a chart of yield trend over time.
 * Honest, vendor-neutral body; product only appears in the closing block.
 * All numbers are illustrative.
 */
export const oeeYieldFirstPassMeasuringWhatMovesTheNeedle: Article = {
  slug: "oee-yield-first-pass-measuring-what-moves-the-needle",
  title: "OEE, yield, first-pass: measuring what actually moves the needle",
  deck:
    "A plant can drown in metrics and still not know how it is doing. A foundry really needs a small set of honest numbers — and the discipline to define them the same way twice. Here are the ones that earn their place on the board.",
  goal: "ROI",
  readingMins: 8,
  heroStat: { value: "3", label: "headline metrics that tell you most of what you need" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "There are two ways to fail at measuring a plant. The first is to measure nothing, and run on gut feel and the loudest voice in the morning meeting. The second, more modern failure is to measure everything — a dashboard with forty gauges that no one can summarise and no two people define the same way. The fix for both is the same: pick a small set of metrics that genuinely move the needle, define each one precisely, and compute it the same way every single time. For a foundry, three numbers do most of the work — overall equipment effectiveness, yield, and first-pass rate — provided you are honest about what each one means.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The headline three",
      heading: "What to put on the board, and what each one tells you",
      sub: "Each answers a different question. None of them answers all three.",
    },
    {
      kind: "metricRow",
      metrics: [
        {
          value: "OEE",
          label: "Overall Equipment Effectiveness",
          caption: "Availability × Performance × Quality. How much of your capacity you actually turned into good output.",
          tone: "brand",
        },
        {
          value: "Yield",
          label: "Metal & material yield",
          caption: "Good casting weight as a share of metal poured. Where your melt and material actually went.",
          tone: "accent",
        },
        {
          value: "FPY",
          label: "First-Pass Yield",
          caption: "Share of castings that pass without rework or rectification. The cost of getting it right once.",
          tone: "success",
        },
      ],
    },
    {
      kind: "paragraph",
      text:
        "Start with OEE, because it is the most abused. Overall equipment effectiveness is the product of three fractions: availability (was the equipment running when it should have been?), performance (was it running at its rated speed?), and quality (were the parts it made good?). Multiplied together they give one honest number for how much of your theoretical capacity actually became saleable output. The trap is that the same letters hide wildly different definitions — does planned downtime count against availability? does rework count as a quality loss? OEE is only useful if everyone agrees on the answers and never quietly changes them to make a bad month look better.",
    },
    {
      kind: "paragraph",
      text:
        "Yield is the metric a foundry feels most directly, because metal is money. Material yield is the weight of good castings divided by the weight of metal melted, and the gap between them is everything that did not ship — runners, risers, gates, spillage, and scrap. A foundry running 65% yield is melting roughly three tonnes to ship two, and every point of yield recovered is melt energy and alloy you stop paying for twice. Yield rewards better gating and methoding as much as it rewards fewer defects, which is what makes it such a complete measure of how efficiently the plant turns raw metal into product.",
    },
    {
      kind: "paragraph",
      text:
        "First-pass yield — sometimes first-pass rate — is the quietest of the three and often the most revealing. It is the share of castings that pass inspection the first time, with no rework, no grinding-out of a defect, no rectification. A plant can post a respectable final quality number while hiding an expensive habit of rescuing marginal parts. First-pass yield strips that disguise away: it counts only the castings that were right the first time, and so it exposes the true cost of a process that needs constant rescuing. Where final yield says 'we got there', first-pass says 'at what hidden cost'.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "A metric you redefine is a metric you cannot trust",
      text:
        "The single biggest failure in plant metrics is not picking the wrong number — it is computing the right number inconsistently. If 'yield' means metal yield this month and casting yield next month, the trend is fiction. Lock the definition, write it down, and compute it the same way forever.",
    },
    {
      kind: "pullQuote",
      quote:
        "You cannot improve what you measure three different ways. The first job of a metric is to mean the same thing twice.",
      attribution: "A rule worth taping to the wall",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Why the trend beats the number",
      heading: "A single reading is gossip; a trend is evidence",
    },
    {
      kind: "paragraph",
      text:
        "A metric quoted once, in isolation, is almost useless — 67% yield means nothing without knowing whether last month was 64% or 71%. The value of these numbers is entirely in their trend: the direction, the slope, and whether a change you made actually bent the line. This is why consistency of definition matters so much. A trustworthy trend requires that every point on the line was computed identically; the moment the definition drifts, the trend becomes an artefact of bookkeeping rather than a signal about the plant. Measure the same thing the same way, month after month, and the line starts telling you the truth about whether you are getting better.",
    },
    {
      kind: "chart",
      chart: "line",
      title: "Illustrative: material yield trend across a year",
      xKey: "month",
      unit: "%",
      series: [{ dataKey: "yield", label: "Material yield", tone: "success" }],
      data: [
        { month: "Jan", yield: 64 },
        { month: "Feb", yield: 64.5 },
        { month: "Mar", yield: 65 },
        { month: "Apr", yield: 66 },
        { month: "May", yield: 66.5 },
        { month: "Jun", yield: 68 },
        { month: "Jul", yield: 69 },
        { month: "Aug", yield: 69.5 },
        { month: "Sep", yield: 71 },
      ],
      caption:
        "Generic, illustrative figures — not a specific plant's results. The slow, sustained climb is what a working improvement programme looks like; the slope matters more than any single point.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The watch-outs",
      heading: "Metrics that quietly mislead",
    },
    {
      kind: "paragraph",
      text:
        "A few honest warnings. Beware vanity metrics that always look good and never drive a decision — total tonnes poured rises with demand and tells you little about how well you ran. Beware metrics that can be gamed: chase availability alone and someone will run equipment past a sand change to keep the clock ticking, trading quality for a better number. And beware averaging away the signal — a monthly OEE that blends a heroic week with a disastrous one hides exactly the variation you needed to see. The cure in every case is to pair each headline metric with the granularity beneath it, so the average never becomes an alibi.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: OEE broken into its three factors",
      unit: "%",
      max: 100,
      bars: [
        { label: "Availability", value: 88, tone: "accent" },
        { label: "Performance", value: 92, tone: "brand" },
        { label: "Quality", value: 94, tone: "success" },
        { label: "OEE (the product)", value: 76, tone: "warning", display: "≈ 76%" },
      ],
      caption:
        "Generic, illustrative split. Three healthy-looking factors still multiply to a humbler OEE — which is exactly why the breakdown matters more than the headline.",
    },
    {
      kind: "paragraph",
      text:
        "Measuring what moves the needle is mostly an exercise in restraint. Choose OEE for how well you used your capacity, yield for how efficiently you turned metal into product, and first-pass for the hidden cost of rework — then guard those definitions like they are written in law. Three honest numbers, computed the same way every time and read as trends rather than snapshots, will tell you more about your plant than forty gauges ever could. The discipline is not in the dashboard; it is in the definitions behind it.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Choosing the right metrics is judgement; computing them identically, every shift, from data tied to the process is mechanics. The platform handles the mechanics so the numbers stay honest enough to trust.",
      capabilities: [
        {
          title: "One definition, computed every time",
          detail:
            "Deterministic heat, daily and monthly reports compute OEE, yield and first-pass the same way on every run — no spreadsheet drift, no quiet redefinition.",
          icon: "coins",
        },
        {
          title: "Trends you can defend",
          detail:
            "Because every metric is derived from model-attached data, each point on the trend is reproducible and auditable — a line you can take to the board.",
          icon: "lineChart",
        },
        {
          title: "Drill past the average",
          detail:
            "Every headline number is backed by the heats and batches beneath it, so the monthly average never hides the week that actually needed attention.",
          icon: "search",
        },
      ],
    },
  ],
};
